import Vue, { VNode, VueConstructor } from "vue";
import App from "./App.vue";
import router from "./router";
import store from "./store";
import i18n from "./i18n";
import VueGtag from "vue-gtag";
import { ImagePath } from "./plugins/ImagePath";
import { Cart } from "./plugins/Cart";
import { Inventory } from "./plugins/Inventory";
import { Toastr } from "./plugins/Toastr/Toastr";
import { Authenticator } from "./plugins/Authenticator";
import { Http } from "@/plugins/Http";
import { LocalStorage } from "@/plugins/LocalStorage";
import { authenticator } from "@/util/authenticator";
import { setupMocks } from "@/mocks/index";
import VueScrollTo from "vue-scrollto";
import TDSDate from "@/util/TDSDate";
import { formatDateForDisplay } from "@/util/displayDate";
import { Regexes } from "@/plugins/Regexes";
import Countries from "@/util/countries";
import { isPrice, Price } from "./interfaces/entities/Price";
import { Product } from "./interfaces/entities/Product";
import { Roles } from "./interfaces/entities/User"; // Import Roles enum
import "@/directives/role"; // Import the custom v-role directive

declare module "vue/types/vue" {
    interface Vue {
        $roles: typeof Roles;
    }
}

require("dotenv").config();
(window as any).Vue = Vue;
Vue.config.productionTip = false;

Vue.use(ImagePath);
Vue.use(Cart);
Vue.use(Inventory);
Vue.use(Toastr);
Vue.use(Authenticator);
Vue.use(Http);
Vue.use(LocalStorage);
Vue.use(VueScrollTo);
Vue.use(Regexes);
Vue.use(VueGtag, {
    config: { id: process.env.VUE_APP_ANALYTICS_ID }
});

Vue.prototype.$roles = Roles; // Make Roles enum globally available

// TODO: Get currency info from API and make format based on that...

Vue.filter("cryptoFormat", (value: number | string, maximumFractionDigits: number, code: string) => {
    if (typeof value === "string") value = parseFloat(value);
    return new Intl.NumberFormat("en", { maximumFractionDigits }).format(value) + " " + code;
});

Vue.filter("fiatFormat", (price: number | string | Price, currency: string = "USD", showCurrency: boolean = true) => {
    const value = isPrice(price) ? price.priceValue : typeof price === "number" ? price : parseFloat(price);
    const currencyCode = isPrice(price) ? price.currencyCode : currency;
    if (value >= 0) {
        return new Intl.NumberFormat("en", {
            style: showCurrency ? "currency" : "decimal",
            currency: currencyCode || "USD",
            currencyDisplay: "code",
            minimumFractionDigits: 2
        }).format(value);
    } else {
        const formatParts = new Intl.NumberFormat("en", {
            style: showCurrency ? "currency" : "decimal",
            currency: currencyCode,
            currencyDisplay: "code",
            minimumFractionDigits: 2
        }).formatToParts(value * -1);
        return formatParts.reduce((acc: string, curr: { type: string; value: string }) => acc + (curr.type === "literal" ? curr.value + "-" : curr.value), "");
    }
});

Vue.filter("compactFiatFormat", (value: number | string, currencyCode: string = "USD", currencySign: boolean = false) => {
    if (typeof value === "string") value = parseFloat(value);
    if (value >= 0) {
        return new Intl.NumberFormat("en", {
            style: "currency",
            currency: currencyCode,
            currencyDisplay: currencySign ? "symbol" : "code",
            //@ts-ignore - typescript is not supporting the notation for numberFormat yet
            notation: "compact"
        }).format(value);
    } else {
        const formatParts = new Intl.NumberFormat("en", {
            style: "currency",
            currency: currencyCode,
            currencyDisplay: currencySign ? "symbol" : "code",
            //@ts-ignore - typescript is not supporting the notation for numberFormat yet
            notation: "compact"
        }).formatToParts(value * -1);
        return formatParts.reduce((acc: string, curr: { type: string; value: string }) => acc + (curr.type === "literal" ? curr.value + "-" : curr.value), "");
    }
});

Vue.filter("percentageFormat", (value: number | string) => {
    if (typeof value === "string") value = Number(value);
    return (value > 0 ? "+" : "") + value.toFixed(2) + "%";
});

Vue.filter("toPercent", (value: number | string) => {
    if (typeof value === "string") value = Number(value);
    return value * 100 + "%";
});

Vue.filter("timeFormat", (value: number | string, format: string) => {
    if (typeof value !== "number") return value;
    switch (format) {
        case "d": // @ts-ignore
            return Math.round(value / 60 / 24) + " " + i18n.tc("general.timeAppendices.d", Math.round(value / 60 / 24));
        default:
            return value;
    }
});

Vue.filter("dateFormat", (value: number | string, format?: string) => {
    if (typeof value !== "string") return value;
    // @ts-ignore
    return new TDSDate(value).format(format || "DD-MM-YYYY HH:mm");
});

Vue.filter("shortDateFormat", (value: number | string, format?: string) => {
    if (typeof value !== "string") return value;
    // @ts-ignore
    return new TDSDate(value).format(format || "MMM YYYY");
});

Vue.filter("dateFormatDisplay", (value: string, format?: string) => {
    const timeZone = store.state.userTimeZone;
    return formatDateForDisplay(value, format || "DD-MM-YYYY HH:mm", timeZone);
});

Vue.filter("countryName", (value: string) => {
    if (value.toLowerCase() === "www") return "Worldwide Max";
    if (value.toLowerCase() === "wwl") return "Worldwide Lite";
    if (value.toLowerCase() === "wwm") return "Worldwide Max";
    if (value.toLowerCase() === "afr") return "Africa";
    if (value.toLowerCase() === "saf") return "Safari";
    if (value.toLowerCase() === "asa") return "Asia";
    if (value.toLowerCase() === "cat") return "Central Asia";
    if (value.toLowerCase() === "eur") return "Europe";
    if (value.toLowerCase() === "lat") return "Latin America";
    if (value.toLowerCase() === "mea") return "Middle East";
    if (value.toLowerCase() === "noa") return "North America";
    if (value.toLowerCase() === "car") return "Caribbean";
    if (value.toLowerCase() === "oce") return "Oceania";
    if (value.toLowerCase() === "haj") return "Hajj and Umrah";
    if (value.toLowerCase() === "fsp") return "Fan Season Pass";
    if (value.toLowerCase() === "ubm") return "Worldwide (Customized)";
    if (value.toLowerCase() === "sea") return "Southeast Asia";
    if (value.toLowerCase() === "glb") return "Global";
    return Countries.getCountryName(value);
});

Vue.filter("countryNameWithCategory", (product: Product) => {
    const countryName = Vue.filter("countryName")(product.countrySet) as string;
    if (product.packageCategory === "promo") {
        return countryName + " - Promotion";
    }
    if (product.packageCategory === "premium") {
        return countryName + " - Premium";
    }
    return countryName;
});

//  In an older version we got some service workers (empty) registered that broke the cache on Safari.
//  So we are removing all service workers here.
if ("serviceWorker" in navigator) {
    navigator.serviceWorker.getRegistrations().then(function(registrations: readonly ServiceWorkerRegistration[]) {
        if (registrations.length > 0) {
            console.log("[main] Service worker registrations: ", registrations);
            for (const registration of registrations) {
                registration.unregister().then(() => {
                    console.log("[main] Unregistered service worker.", registration);
                });
            }
        }
    });
} else {
    console.warn("[main] Browser does not support service workers!");
}

setupMocks();

async function init() {
    try {
        const accessToken: string | undefined = await authenticator.getAccessToken();
        console.log("[main] Got access token: ", accessToken);
        store.commit("SET_AUTH", accessToken ?? "");
        if (accessToken) {
            await store.dispatch("FETCH_ACCOUNT");
            await store.dispatch("FETCH_ACCOUNT_DETAILS");
        } else {
            window.localStorage.clear();
        }
    } catch (err) {
        window.localStorage.clear();
        store.commit("SET_AUTH", "");
        await authenticator.signOut();
        console.error("[App] Unexpected error on getting access token: ", err);
    }

    const app = new Vue({
        router,
        i18n,
        store,
        render: (h: (component: VueConstructor) => VNode) => h(App)
    });

    // Manually expose the constructor to the devtools hook
    if (typeof window !== "undefined" && (window as any).__VUE_DEVTOOLS_GLOBAL_HOOK__) {
        (window as any).__VUE_DEVTOOLS_GLOBAL_HOOK__.Vue = app.constructor;
    }

    app.$mount("#app");
}

init().then(() => console.log("[main] Application started."));
